import React, { useEffect, useState } from 'react';
import { fmtLocal, timeAgo } from '../../utils/time';
import api, { asArray } from '../../services/api';

const MAX_LENGTH = 100;


/** Admin/semi-admin console for the 100-char broadcast messages that show up on
 *  every user's Notifications tab (Notifications.jsx). Same dark card language
 *  as AdminSignals.jsx / the rest of the admin surface. */
export default function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  // Review queue: drafts auto-written when a stock/option signal is published or
  // closed. Each keeps its own editable text until the admin sends or discards it.
  const [drafts, setDrafts] = useState([]);
  const [draftText, setDraftText] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/notifications')
      .then(res => setItems(asArray(res.data)))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/notifications/drafts')
      .then(res => {
        const list = asArray(res.data);
        setDrafts(list);
        setDraftText(prev => {
          const next = {};
          list.forEach(d => { next[d.id] = prev[d.id] ?? d.message; });
          return next;
        });
      })
      .catch(() => {});
  };

  const sendDraft = (id) => {
    const msg = (draftText[id] || '').trim();
    if (!msg) return;
    setSendingId(id);
    setError('');
    api.post(`/notifications/${id}/send`, { message: msg })
      .then(() => load())
      .catch(err => setError(err?.response?.data?.message || 'Failed to send'))
      .finally(() => setSendingId(null));
  };

  const discardDraft = (id) => {
    api.delete(`/notifications/${id}`)
      .then(() => load())
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const publish = () => {
    const msg = draft.trim();
    if (!msg) return;
    setSaving(true);
    setError('');
    api.post('/notifications', { message: msg })
      .then(() => {
        setDraft('');
        load();
      })
      .catch(err => setError(err?.response?.data?.message || 'Failed to publish'))
      .finally(() => setSaving(false));
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setEditDraft(n.message);
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = (id) => {
    const msg = editDraft.trim();
    if (!msg) return;
    setSaving(true);
    setError('');
    api.put(`/notifications/${id}`, { message: msg })
      .then(() => {
        setEditingId(null);
        setEditDraft('');
        load();
      })
      .catch(err => setError(err?.response?.data?.message || 'Failed to update'))
      .finally(() => setSaving(false));
  };

  const remove = (id) => {
    if (!window.confirm('Delete this notification? Users will no longer see it.')) return;
    api.delete(`/notifications/${id}`)
      .then(() => load())
      .catch(() => {});
  };

  return (
    <div className="pb-8">
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Manage Notifications</h1>
        <p className="text-xs text-slate-500 mt-1">
          Broadcast a short message to every user&rsquo;s Notifications tab (max {MAX_LENGTH} characters).
        </p>
      </div>

      {drafts.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-md overflow-hidden mb-5">
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
            <p className="text-sm font-semibold text-amber-300">Review · {drafts.length} waiting</p>
            <p className="text-[11px] text-slate-500">Auto-written from signal publish / close</p>
          </div>
          <div className="p-2.5 space-y-2">
            {drafts.map(d => {
              const text = draftText[d.id] ?? d.message;
              const isClose = d.eventType === 'CLOSE';
              return (
                <div key={d.id} className="rounded-xl bg-white/[0.03] border border-white/5 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      isClose ? 'bg-sky-400/15 text-sky-300' : 'bg-emerald-400/15 text-emerald-300'
                    }`}>
                      {isClose ? 'CLOSED' : 'PUBLISHED'}
                    </span>
                    <span className="text-[11px] text-slate-500">{fmtLocal(d.createdAt)} · {timeAgo(d.createdAt)}</span>
                  </div>
                  <textarea
                    value={text}
                    onChange={(e) => setDraftText(prev => ({ ...prev, [d.id]: e.target.value.slice(0, MAX_LENGTH) }))}
                    maxLength={MAX_LENGTH}
                    rows={2}
                    className="w-full rounded-lg bg-white/[0.05] border border-white/10 text-sm text-white px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400/60 resize-none"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[10px] tabular-nums ${text.length >= MAX_LENGTH ? 'text-rose-400' : 'text-slate-500'}`}>
                      {text.length}/{MAX_LENGTH}
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => discardDraft(d.id)}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-lg text-rose-300 hover:bg-rose-400/10"
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        onClick={() => sendDraft(d.id)}
                        disabled={sendingId === d.id || !text.trim()}
                        className="text-[11px] font-semibold px-3 py-1 rounded-lg bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 disabled:opacity-40"
                      >
                        {sendingId === d.id ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-md overflow-hidden mb-5">
        <div className="p-4 space-y-2.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_LENGTH))}
            maxLength={MAX_LENGTH}
            rows={2}
            placeholder="Write an announcement for all users…"
            className="w-full rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white placeholder-slate-500 px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-emerald-400/60 resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[11px] tabular-nums ${draft.length >= MAX_LENGTH ? 'text-rose-400' : 'text-slate-500'}`}>
              {draft.length}/{MAX_LENGTH}
            </span>
            <button
              type="button"
              onClick={publish}
              disabled={saving || !draft.trim()}
              className="text-xs font-semibold px-4 py-2 rounded-lg bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {saving ? 'Publishing…' : 'Publish'}
            </button>
          </div>
          {error && <p className="text-[11px] text-rose-400">{error}</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-md overflow-hidden">
        <div className="p-2.5 space-y-1.5">
          {loading ? (
            <p className="text-center text-slate-500 text-xs py-8">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-500 text-xs py-8">No notifications published yet.</p>
          ) : (
            items.map(n => (
              <div
                key={n.id}
                className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 transition"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-emerald-400/15 text-emerald-300">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  {editingId === n.id ? (
                    <div className="space-y-1.5">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value.slice(0, MAX_LENGTH))}
                        maxLength={MAX_LENGTH}
                        rows={2}
                        className="w-full rounded-lg bg-white/[0.05] border border-white/10 text-sm text-white px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-400/60 resize-none"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] tabular-nums ${editDraft.length >= MAX_LENGTH ? 'text-rose-400' : 'text-slate-500'}`}>
                          {editDraft.length}/{MAX_LENGTH}
                        </span>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg text-slate-300 hover:bg-white/10"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(n.id)}
                            disabled={saving || !editDraft.trim()}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 disabled:opacity-40"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-white leading-snug">{n.message}</p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {fmtLocal(n.createdAt)} · {timeAgo(n.createdAt)}
                        {n.publishedBy ? ` · ${n.publishedBy}` : ''}
                        {n.updatedAt && n.updatedAt !== n.createdAt ? ' · edited' : ''}
                      </p>
                    </>
                  )}
                </div>
                {editingId !== n.id && (
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(n)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-lg text-sky-300 hover:bg-sky-400/10"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(n.id)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-lg text-rose-300 hover:bg-rose-400/10"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
